import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  EnvelopeSimple,
  Users,
} from "@phosphor-icons/react";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import { ContourBackground } from "@/components/marketing/SresVisuals";
import useSEO from "@/lib/useSEO";

/**
 * Mobile-first /forgot-password page — matches the /login and /signup grammar.
 * Explains the SRES admin-mediated password reset flow.
 */
export default function ForgotPasswordPage() {
  useSEO({
    title: "Reset your password",
    description:
      "SRES password resets are handled by your workspace administrator. Here's exactly how to request one.",
    path: "/forgot-password",
  });

  return (
    <div
      className="min-h-screen bg-white flex flex-col overflow-x-hidden"
      data-testid="forgot-password-page"
    >
      <MarketingHeader />

      <main
        className="flex-1 relative w-full px-5 sm:px-6 py-10 sm:py-14 overflow-hidden"
        style={{
          background:
            "radial-gradient(900px 600px at 50% -10%, rgba(30,111,255,0.08), transparent 60%), #FFFFFF",
        }}
      >
        <div
          className="absolute inset-0 grid-lines opacity-40 pointer-events-none"
          aria-hidden
        />
        <div
          className="absolute left-1/2 -translate-x-1/2 -top-20 w-[720px] max-w-none h-[560px] opacity-[0.55] pointer-events-none"
          aria-hidden
        >
          <ContourBackground />
        </div>

        <div className="relative w-full max-w-[520px] mx-auto flex flex-col gap-6">
          {/* Brand block */}
          <div className="text-center pt-2">
            <div className="flex justify-center">
              <img
                src="/sres-logo-horizontal.png?v=5"
                alt="SRES · Shanvi Resource Estimation Studio"
                className="h-12 sm:h-14 w-auto object-contain max-w-full"
                draggable={false}
              />
            </div>
            <div className="mt-5 inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
              <span className="w-6 h-px bg-brand-blue" />
              Password reset
            </div>
            <h1
              className="mt-4 font-heading font-black text-slate-900 text-3xl sm:text-4xl tracking-[-0.02em]"
              data-testid="forgot-heading"
            >
              Forgot your password?
            </h1>
            <p className="mt-4 text-slate-600 text-[14.5px] leading-[1.7] max-w-md mx-auto">
              SRES is a gated workspace — every account is provisioned and its
              credentials managed by your organisation&apos;s administrator.
              Here&apos;s what happens next.
            </p>
          </div>

          {/* Card */}
          <div
            className="bg-white border border-slate-200 rounded-2xl shadow-[0_20px_60px_-30px_rgba(15,23,42,0.18)] p-5 sm:p-8"
            data-testid="forgot-card"
          >
            <ol className="space-y-5" data-testid="forgot-steps">
              <Step
                n={1}
                icon={EnvelopeSimple}
                title="Ask your administrator to reset it."
                body="Your workspace admin can issue a temporary password from the Super Admin Console in seconds. Not sure who your admin is — we can put you in touch."
              />
              <Step
                n={2}
                icon={ShieldCheck}
                title="Receive a one-time temporary password."
                body="You'll get a short-lived password to sign in with. It's marked as 'must-change' — SRES will force you to rotate it before you can access the workspace."
              />
              <Step
                n={3}
                icon={Users}
                title="Choose a new password on first sign-in."
                body="Minimum 8 characters. Store it in a password manager. You're back in — no support tickets, no email loops."
              />
            </ol>

            {/* CTAs — stack on mobile, side-by-side on ≥sm */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact?topic=password-reset"
                className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-md bg-slate-900 hover:bg-black text-white font-semibold text-[14px] transition-colors"
                data-testid="forgot-cta-contact"
              >
                Contact support
                <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/login"
                className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-md border border-slate-300 text-slate-800 hover:border-slate-900 hover:text-slate-900 font-semibold text-[14px] transition-colors"
                data-testid="forgot-cta-back"
              >
                Back to sign in
              </Link>
            </div>
          </div>

          <p className="text-center text-[12px] text-slate-500 leading-relaxed">
            Super-administrator? Reset any user&apos;s password directly from{" "}
            <Link
              to="/app/superadmin"
              className="text-brand-blue font-medium hover:underline"
            >
              the console
            </Link>
            .
          </p>
        </div>
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Step({ n, icon: Icon, title, body }) {
  return (
    <li className="flex items-start gap-4">
      <div className="shrink-0 relative">
        <div className="w-11 h-11 rounded-xl border border-slate-200 flex items-center justify-center">
          <Icon size={17} weight="regular" className="text-slate-700" />
        </div>
        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center font-mono">
          {n}
        </div>
      </div>
      <div className="flex-1 pt-1">
        <div className="text-[14.5px] font-semibold text-slate-900 leading-snug tracking-tight">
          {title}
        </div>
        <p className="mt-1.5 text-[13.5px] text-slate-600 leading-[1.7]">
          {body}
        </p>
      </div>
    </li>
  );
}
